import booleanIntersects from '@turf/boolean-intersects';
import kinks from '@turf/kinks';
import * as DKFDS from 'dkfds'
import $ from 'jquery';
import moment from 'moment';
import Feature from 'ol/Feature';
import TileLayer from 'ol/layer/Tile';
import { TileWMS } from 'ol/source';
import { getEnv } from './environment';
import {
    address,
    blockingAdvanced,
    blockingSimple,
    busruter,
    cvf,
    frededeVeje,
    hastighed,
    helkm,
    municipalityBorders,
    ortofoto,
    politikredse,
    topoSkaermKort
} from './layers';
import { fdsShowError } from './modals';
import { initSeptimaSearch } from './septima';
import { forEachSite, getSiteGeometry, getSiteVal, hasAuthorityCode, hasNoGeometry } from './sites';
import { showAreaSearchResult } from './ui/add-streets-modal';
import { has, ISO_DATE_FORMAT, siteFieldSelector, zeroPad } from './utils';

/*global VDMap, OLUtils, authority, VDMapTileGrid, authoritycode */

const cvfLayerMaxResolution = 70;
const helkmLayerMaxResolution = 5;
const addressLayerMaxResolution = 5;

//region EXPORTS

/**
 * Injects a map component.
 *
 * @param {string} elementId - ID of element to insert map component in
 * @param {jQuery} referencingUrls - List of inputs containing referencing URLs
 * @param {string[]} mapModes - List of map tools to include
 * @param {Object.<string, function>} handlers - Map callback functions
 * @param {{}[]} layers - List of additional foreground layers
 * @returns {Promise.<VDMap>}
 */
export async function createMap(elementId, referencingUrls, mapModes, handlers, layers) {
    if (mapModes.length > 0) {
        $(`#${elementId}-mapinfo`).show().find(mapModes.map(value => `.${value}`).join(', ')).css('display', 'list-item');
    }

    const env = await getEnv();
    const bgLayers = [
        topoSkaermKort(),
        ortofoto()
    ];

    let vdMap;

    const cvfLayer = cvf(cvfLayerMaxResolution);
    cvfLayer.on('change:visible', () => toggleCVFLegend(vdMap, cvfLayer));

    const hastighedLayer = hastighed();
    hastighedLayer.on('change:visible', () => toggleHastighedLegend(vdMap, hastighedLayer));

    // This callback is only used in selectSections mode
    if (handlers.addPolygonCallback === undefined) {
        handlers.addPolygonCallback = function (polygon) {
            vdMap.addGeometry(polygon);
        };
    }

    vdMap = new VDMap($('#' + elementId)[0], {
        bgLayers,
        fgLayers: [
            address(addressLayerMaxResolution),
            cvfLayer,
            helkm(helkmLayerMaxResolution),
            hastighedLayer,
            busruter(),
            frededeVeje(),
            blockingAdvanced(),
            blockingSimple(),
            municipalityBorders(),
            politikredse()
        ].concat(layers || []),
        referencingUrl: env.searchUrl + "/public/referencing",
        pathFindingUrl: env.searchUrl + "/pathfinding",
        // conflictGeoserverUrl: env.oov2Url + '/OOV/wms',
        modifySectionCallback: handlers.modifySectionCallback,
        addPolygonCallback: handlers.addPolygonCallback,
        addSectionCallback: handlers.addSectionCallback,
        addRouteCallback: handlers.addRouteCallback,
        onUserAddPolygonCallback: validatePolygon,
        changePolygonCallback: validateChangedPolygon,
        activeModes: mapModes,
        useLayerSwitcher: true,
        baseUrl: '/sukort/',
        attributes: 'Tilladelse_Vedligeholder.kode, Bestyrer.kode, CPR_vejnavn, Vejbeskrivelse, VejStiStatus',
        coord2roadUrl: '/vejman/services/coord2road', // <-- coord2road is broken on udv without login, so don't use proxy for now
        snaptolerance: 30,
        debounceDelay: env.debounceDelay,
        showConflictLayer: false
    });

    // This is not optimal, but buildAddSectionCallback needs a reference to vdMap, and options.addSectionCallback is not actually read in the constructor of vdMap
    vdMap.options.addSectionCallback = handlers.addSectionCallback || buildAddSectionCallback(vdMap);

    vdMap.map.getView().on('change:resolution', function() {
        toggleCVFLegend(vdMap, cvfLayer);
    });
    vdMap.map.getView().padding = [20, 20, 20, 20];

    persistState(vdMap.map.getLayerGroup());

    global.vdMap = vdMap; //TODO: necessary?

    handleReferencingUrls(referencingUrls, vdMap);

    return vdMap;
}

export async function createConflictMap(elementId) {
    $(`#${elementId}-mapinfo`).show().find('.showInfo').css('display', 'list-item');

    const env = await getEnv();

    const bgLayers = [
        topoSkaermKort(),
        ortofoto()
    ];

    const vdMap = new VDMap($('#' + elementId)[0], {
        bgLayers: bgLayers,
        conflictGeoserverUrl: env.oov2Url + '/OOV/wms',
        activeModes: ['showInfo'],
        useLayerSwitcher: true,
        baseUrl: '/sukort/',
        showConflictLayer: true
    });
    vdMap.map.getView().padding = [20, 20, 20, 20];

    handleReferencingUrls(null, vdMap);

    return vdMap;
}

export async function getExtraLayers(authority) {
    const request = {
        url: '/vejman/services/layerservlet',
        data: {
            // WARNING: the authority value used is broker authority, except everything < 100 is mapped to 0
            //          this *will* fail for all pseudo-non-vd-authorities
            authority: authority
        },
        dataType: 'json',
        timeout: 1000
    };
    // Explicitly awaiting here to return ES6 Promise instead of jQuery Promise
    return await $.ajax(request).then(buildLayers, handleLayerServletError);
}

/**
 * Retrieves system line for a given section.
 * TODO: rewrite as async
 *
 * @param {string} path - A section specification on the form '/&lt;vejnr&gt;/&lt;vejdel&gt;?from=&lt;frakmt&gt;&to=&lt;tilkmt&gt;'
 * @returns {Promise} A promise of a feature collection
 */
export function getSysLine(path) {
    return new Promise((resolve, reject) => {
        getEnv().then(function (env) {
            const url = env.searchUrl + '/public/referencing/sysline' + path;
            fetch(url)
                .then(response => response.json())
                .then(json => {
                    $.each(json.features, (_index, feature) => {
                        // Patch broken GeoJSON from sysline service
                        if (feature.type === 'feature') {
                            feature.type = 'Feature';
                        }
                    });
                    resolve(json);
                })
                .catch(reason => {
                    console.error("Error requesting '%s': %o", url, reason);
                    reject(reason);
                });
        });
    });
}

/**
 * Given a sysline path, add sysline to map and zoom to contents.
 * TODO: rewrite as async
 *
 * @param {string} path
 * @param {VDMap} vdMap
 * @returns {Promise} The sysline feature (collection)
 */
export function injectSection(path, vdMap) {
    return getSysLine(path).then(function (response) {
        $.each(response.features, function (_index, feature) {
            vdMap.addSection(feature, true, false);
        });
        vdMap.zoomToContents();
    });
}

export function onChooseSitesMapCreated(vdMap) {
    // noinspection JSIgnoredPromiseFromCall
    initSeptimaSearch(vdMap);
    setupShowOnMapHandlers(vdMap);
    if (hasNoGeometry() && hasAuthorityCode()) {
        zoomToAuthority(vdMap);
    }
}

export function shinyNewZoomer(vdMap, referencingUrl, geometry) {
    function doHighlightAndZoom(response) {
        if (response) {
            response.features.forEach(function (feature) {
                vdMap.highlightGeometry(feature);
            });
        }
        processGeometry(geometry, vdMap.highlightGeometry.bind(vdMap));
        vdMap.map.getView().fit(
            vdMap.highlightOverlay.getSource().getExtent(),
            vdMap.map.getSize()
        );
        setTimeout(vdMap.clearHighlights.bind(vdMap), 5000);
    }

    vdMap.clearHighlights();
    if (referencingUrl) {
        getSysLine(referencingUrl).then(doHighlightAndZoom);
    } else {
        doHighlightAndZoom();
    }
}

export function showSiteDetailMap(element) {
    const id = element.id;
    const siteIndex = $(element).data('index');
    const referencingUrl = $('input[name=' + id + 'ReferencingUrl]');

    const activeModes = ['drawPolygon', 'drawLine', 'deletePolygon', 'dirInfo', 'streetView'];
    if (referencingUrl.val()) {
        activeModes.push('modifySection');
    }

    getExtraLayers(authority)
        .then(layers => showMapWithLayers(id, siteIndex, referencingUrl, activeModes, layers));
}

export function setConflictLayerFilter(from, to, vdMapPromise) {
    const params = [];
    if (from) {
        params.push(`enddate > ${moment(from, ["DDMMYYYY", "YYYYMMDD"]).format(ISO_DATE_FORMAT)}`);
    }
    if (to) {
        params.push(`startdate < ${moment(to, ["DDMMYYYY", "YYYYMMDD"]).format(ISO_DATE_FORMAT)}`);
    }

    if (params.length > 0) {
        vdMapPromise.then(vdMap => {
            vdMap.conflictLayer.getSource().updateParams({
                CQL_FILTER: params.join(' and ')
            });
            vdMap.conflictLayer.setVisible(true);
            vdMap.map.getLayers().item(1).setVisible(true);
        })
    } else {
        vdMapPromise.then(vdMap => vdMap.conflictLayer.setVisible(false));
    }
}

//endregion

//region INTERNALS

function validatePolygon(feature) {
    const geometries = OLUtils.geoJSON.writeFeaturesObject(this.getGeometries());
    const last = geometries.features.pop();
    const intersects = geometries.features.some(f => booleanIntersects(last, f));
    const hasKinks = kinks(last).features.length > 0;
    if (intersects || hasKinks) {
        alert("Området må ikke overlappe andre områder, og siderne i området må ikke krydse hinanden.")
        this.removeGeometry(feature);
    }
}

function validateChangedPolygon(feature) {
    const geometries = OLUtils.geoJSON.writeFeaturesObject(this.getGeometries().filter(f => f !== feature));
    const featureJson = OLUtils.geoJSON.writeFeatureObject(feature);
    const intersects = geometries.features.some(f => booleanIntersects(featureJson, f));
    const hasKinks = kinks(featureJson).features.length > 0;
    if (intersects || hasKinks) {
        alert("Området må ikke overlappe andre områder, og siderne i området må ikke krydse hinanden.")
        return false;
    }
}

async function convertSection(section) {
    function getAuthority(props) {
        let authority = props.authority;
        if (authority == null) {
            authority = props.sysLine.features[0].properties.authority;
        }
        return authority + '';
    }

    const properties = section.getProperties();
    const moreProperties = await getMoreProperties(properties);
    if (moreProperties &&
        moreProperties.authority === null &&
        moreProperties.authorityName === null &&
        moreProperties.streetName === null &&
        moreProperties.cprStreetName === null) {
        return null;
    }
    let authorityName = '(Ukendt myndighed)';
    let roadName = '';
    if (moreProperties && moreProperties.authority !== null) {
        roadName = moreProperties.authority === 0 ? moreProperties.streetName : moreProperties.cprStreetName;
        if (roadName === 'undefined' || roadName === null || roadName.trim().length === 0) {
            roadName = '(Ukendt vejnavn)';
        }
        authorityName = moreProperties.authorityName;
    }
    return {
        searchType: 'vejman',
        authority: getAuthority(properties),
        authorityName: authorityName,
        displayName: roadName,
        'VEJ:VEJBESKRIVELSE': roadName,
        roadNumber: properties.road.number,
        roadPart: properties.road.part,
        fromKmt: properties.from.kmt,
        toKmt: properties.to.kmt,
        wkt: OLUtils.WKT.writeFeature(section)
    };
}

async function getMoreProperties(properties) {
    const params = 'roadNo=' + properties.road.number + '&roadPart=' + properties.road.part + '&fromKmt=' + properties.from.kmt + '&toKmt=' + properties.to.kmt;
    const request = new Request('/get-linear-search-properties?' + params);
    const response = await fetch(request);
    return response.json();
}

/**
 * @param {VDMap} vdMap
 * @returns {function([{}])}
 */
function buildAddSectionCallback(vdMap) {
    return async function (sections) {
        vdMap.removeGeometry(null);

        const $modal = $('#addStreetsModal');
        if (sections && sections.length > 0) {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }

            new DKFDS.Modal($modal.get(0)).show();

            const convertedSections = [];
            for (const section of sections) {
                const f = OLUtils.toFeature(section);
                const convSec = await convertSection(f);
                if (convSec !== null) {
                    convertedSections.push(convSec);
                }
            }

            $('#loading').hide();

            // Behaviour changed so even if there is only one road found the modal will still be shown
            // Earlier, if only one road were found it would be added automatically to sites
            if (convertedSections.length > 0) {
                showAreaSearchResult(convertedSections);
            } else {
                fdsShowError($modal, 'Fejl under opslag af strækninger.')
                $('#addStreetsModalOK').attr('disabled', true);
            }
        } else {
            console.log('Ingen veje fundet i det valgte område');
            // $('#addedSites').val('noResult').closest('form').submit()
        }
    };
}

function buildLayers(result) {
    const layers = [];
    for (const element of result.data) {
        const data = element;
        const params = JSON.parse(data.params);
        // noinspection JSUnresolvedFunction
        const layer = new TileLayer({
            title: data.title,
            visible: false,
            source: new TileWMS({
                url: data.url,
                params
            }),
            tileGrid: VDMapTileGrid
        });
        layers.push(layer);
    }
    return layers;
}

/**
 * @param {{ code, section, from, to, geometry, caseIndex, siteIndex }} site
 */
async function getSysLineForSite(site) {
    const path = '/' + site.code + '/' + site.section + '?from=' + site.from + '&to=' + site.to;
    const sysLine = await getSysLine(path);
    sysLine.features.forEach(feature => {
        feature.properties.caseIndex = site.caseIndex;
        feature.properties.siteIndex = site.siteIndex;
    });
    return sysLine;
}

function handleLayerServletError(error) {
    console.error('Layer servlet returned invalid data or timed out: %o', error);
    // if we get an error or timeout, continue without extra layers
    return [];
}

export function handleReferencingUrls(referencingUrls, vdMap) {
    if (referencingUrls === null) {
        // new code - null means to use the 'sites' array instead
        forEachSite(site => {
            if (site.code) {
                getSysLineForSite(site).then(response => {
                    response.features.forEach(feature => vdMap.addSection(feature, true, false));
                    vdMap.zoomToContents();
                });
            }
            if (site.geometry) {
                processGeometry(
                    site.geometry,
                    geometry => vdMap.addGeometry(geometry),
                    () => vdMap.zoomToContents(),
                    site.caseIndex,
                    site.siteIndex
                );
            }
        });
    }
    else if (referencingUrls.length >= 1) {
        //TODO: remove this branch when we get rid of the last referencing urls
        $.each(referencingUrls, function (_index, input) {
            if (input.value !== '') {
                // noinspection JSIgnoredPromiseFromCall
                injectSection(input.value, vdMap);
            }
        });
    }
}

function onMapCreated(id, siteIndex, vdMap) {
    $('#' + id).data('vdMap', vdMap);

    vdMap.map.getLayerGroup().on('change', function () {
        let geometry = OLUtils.WKT.writeFeatures(vdMap.getGeometries());
        // Oracle doesn't seem to support empty.
        if (geometry.indexOf("EMPTY") > -1) {
            geometry = "";
        }
        $("#" + id + "geometry").val(geometry);
    });

    const postalCode = $(siteFieldSelector("postalCode", siteIndex)).val();
    if (has(postalCode)) {
        const url = `https://api.dataforsyningen.dk/postnumre/${postalCode}?srid=25832`;
        $.getJSON(url, null, function (response) {
            vdMap.map.getView().fit(response.bbox, vdMap.map.getSize());
        }).fail(function (_jqxhr, _textStatus, error) {
            console.error("error requesting '" + url + "': " + error);
        });
    }

    const wkt = $('#' + id + 'geometry');
    if (wkt.length > 0 && wkt.val().length > 0) {
        processGeometry(wkt.val(), vdMap.addGeometry.bind(vdMap), vdMap.zoomToContents.bind(vdMap));
    }

    $('button[name=zoomToSite][data-index]').each(function () {
        $(this).on('click', zoomToSite.bind(this, vdMap));
    });
}

function persistState(layer) {
    if (layer.getLayers) {
        // Group
        layer.getLayers().forEach(persistState);
    } else {
        // Single layer
        const title = layer.get('title');
        if (title) {
            const savedState = JSON.parse(localStorage.getItem(title));

            if (savedState !== null) {
                layer.setVisible(savedState);
            }
            layer.on('change:visible', function () {
                localStorage.setItem(title, JSON.stringify(layer.getVisible()));
            });
        }
    }
}

/**
 * Converts 'geometry' to a feature, calls 'first' with that feature, or sequentially with every feature in the
 * collection if geometry is a collection, and finally calls 'then'.
 *
 * @param {string|object} geometry - GML, WKT or GeoJSON geometry
 * @param {function} first - Callback called with every non-collection feature read
 * @param {function} [then] - Callback called after every call to 'first'
 * @param {number} [caseIndex] - Case index
 * @param {number} [siteIndex] - Site index
 */
function processGeometry(geometry, first, then, caseIndex, siteIndex) {
    if (geometry && geometry !== 'GEOMETRYCOLLECTION EMPTY') {
        const f = OLUtils.toFeature(geometry);
        if (f) {
            if (f.getGeometry().getType() === "GeometryCollection") {
                f.getGeometry().getGeometries().forEach(g => {
                    first(new Feature({ geometry: g, caseIndex, siteIndex }));
                });
            } else {
                f.setProperties({ caseIndex, siteIndex });
                first(f);
            }
            if (then) {
                then();
            }
        }
    }
}

function setupShowOnMapHandlers(vdMap) {
    $('button[name=showOnMap]').each(function () {
        $(this).on('click', function () {
            const c = this.dataset.caseIndex;
            const s = this.dataset.siteIndex;
            const streetCode = getSiteVal(c, s, 'administrativeStreetCode');
            const streetSection = getSiteVal(c, s, 'streetSection');
            const fromKmt = getSiteVal(c, s, 'chainage.fromKm') + zeroPad(getSiteVal(c, s, 'chainage.fromM'), 4);
            const toKmt = getSiteVal(c, s, 'chainage.toKm') + zeroPad(getSiteVal(c, s, 'chainage.toM'), 4);
            const referencingUrl = streetCode !== '' ? '/' + streetCode + '/' + streetSection + '?from=' + fromKmt + '&to=' + toKmt : null;
            const geometry = getSiteGeometry(c, s);
            shinyNewZoomer(vdMap, referencingUrl, geometry);
            const position = $(vdMap.container).position();
            scrollTo(position.left, position.top)
        });
    });
}

function showMapWithLayers(id, siteIndex, referencingUrl, mapModes, layers) {
    const handlers = {
        "modifySectionCallback": feature => updateKmtAndReferencingUrl(siteIndex, feature)
    };

    createMap(id + "map", referencingUrl, mapModes, handlers, layers)
        .then((vdMap) => onMapCreated(id, siteIndex, vdMap));
}

function toggleCVFLegend(vdMap, cvfLayer) {
    const zoomLevel = vdMap.map.getView().getZoom();
    if (zoomLevel >= 3 && cvfLayer.getVisible()) {
        $(".legendToggle").show();
    } else {
        $(".legendToggle").hide();
    }
}

function toggleHastighedLegend(vdMap, hastighedLayer) {
    if (hastighedLayer.getVisible()) {
        $(".hastighed-legend").show();
    } else {
        $(".hastighed-legend").hide();
    }
}

function updateKmtAndReferencingUrl(siteIndex, feature) {
    const properties = feature.getProperties();

    $(siteFieldSelector('chainage.fromKm', siteIndex)).val(properties.from.km);
    $(siteFieldSelector('chainage.fromM', siteIndex)).val(properties.from.m);
    $(siteFieldSelector('chainage.toKm', siteIndex)).val(properties.to.km);
    $(siteFieldSelector('chainage.toM', siteIndex)).val(properties.to.m);

    const administrativeStreetCode = $(siteFieldSelector('administrativeStreetCode', siteIndex)).val();
    const streetSection = $(siteFieldSelector('streetSection', siteIndex)).val();
    const chainageFromKMT = properties.from.km + zeroPad(properties.from.m, 4);
    const chainageToKMT = properties.to.km + zeroPad(properties.to.m, 4);
    $("input[name='sted" + siteIndex + "ReferencingUrl']").val(
        `/${administrativeStreetCode}/${streetSection}?from=${chainageFromKMT}&to=${chainageToKMT}`
    );
}

function zoomToAuthority(vdMap) {
    $.getJSON(
        `https://api.dataforsyningen.dk/kommuner/${authoritycode}?srid=25832`,
        null,
        function (response) {
            vdMap.map.getView().fit(response.bbox, vdMap.map.getSize());
        }
    ).fail(function (_jqxhr, textStatus, error) {
        console.error('Fejl under hentning af kommunepolygon', textStatus, error);
    });
}

function zoomToSite(vdMap) {
    const siteIndex = $(this).data('index');
    // if ($("#sted" + siteIndex).hasClass('in')) {
        const referencingUrl = $('input[name=sted' + siteIndex + 'ReferencingUrl]').val();
        const geometry = $('#sted' + siteIndex + 'geometry').val();
        shinyNewZoomer(vdMap, referencingUrl, geometry);
    // }
}

//endregion
